import { I18n } from 'aws-amplify';
import { Formik, FormikHelpers, FormikProps, useFormikContext } from 'formik';
import React from 'react';
import * as Yup from 'yup';

import { MFA_CODE_LENGTH } from 'src/constants/authConsts';
import OtpInput from 'src/legacy/components/Auth/OtpInput';
import { SharpOutlinedTextField } from 'src/legacy/components/TextField';

import OnboardingButton from '../OnboardingButton';

import { OnboardingButtonContainer } from './OnboardingButtonContainer';
import {
  OnboardingLoginChallengeFormValues,
  OnboardingLoginFormProps,
} from './onboardingFormTypes';

export const onboardingLoginChallengeFormSchema = Yup.object()
  .strict(true)
  .shape({
    challengeResponse: Yup.string()
      .required('Code must be exactly 6 digits')
      .matches(/^[0-9]+$/, 'Code must be only digits')
      .min(6, 'Code must be exactly 6 digits')
      .max(6, 'Code must be exactly 6 digits'),
  });

interface OnboardingLoginChallengeProps {
  challengeName?: string;
}

export const OnboardingLoginChallenge = ({
  challengeName,
}: OnboardingLoginChallengeProps) => {
  const {
    initialValues,
    errors,
    handleBlur,
    handleChange,
    touched,
    values,
    isSubmitting,
    setFieldValue,
    submitForm,
  } = useFormikContext<OnboardingLoginChallengeFormValues>();

  const isMfaChallenge = challengeName === 'SOFTWARE_TOKEN_MFA';

  return (
    <>
      {isMfaChallenge ? (
        <OtpInput
          value={values.challengeResponse}
          onChange={(otp) => {
            void (async () => {
              // although Formik's types don't reflect this, setFieldValue is async

              await setFieldValue('challengeResponse', otp, true);

              // auto-verify otp when last digit is filled
              if (otp.length === MFA_CODE_LENGTH) {
                await submitForm();
              }
            })();
          }}
          error={touched.challengeResponse && errors.challengeResponse}
          className="pt-10"
        />
      ) : (
        <SharpOutlinedTextField
          InputProps={{
            'data-testid': 'challengeResponseInput',
          }}
          sizeVariant="tall"
          id="login-challengeResponse"
          autoFocus={!initialValues.email}
          fullWidth
          type="text"
          key="challengeResponse"
          name="challengeResponse"
          variant="outlined"
          onBlur={handleBlur}
          onInput={handleChange}
          value={values.challengeResponse}
          label={I18n.get('Verification code')}
          placeholder={I18n.get('Verification code')}
          error={Boolean(touched.challengeResponse && errors.challengeResponse)}
          helperText={
            (touched.challengeResponse && errors.challengeResponse) || ' '
          }
          autoComplete="off"
        />
      )}
      <OnboardingButtonContainer>
        <OnboardingButton
          type="submit"
          isLoading={isSubmitting}
          data-testid="submit-button"
          color="primary"
          progressColor={isMfaChallenge ? 'primary' : 'secondary'}
          variant={isMfaChallenge ? 'outlined' : 'contained'}
          fullWidth
          htmlId="submit-challenge-response-button"
          disabled={
            isMfaChallenge &&
            values.challengeResponse?.length !== MFA_CODE_LENGTH
          }
        >
          {I18n.get(isMfaChallenge ? 'Verify Token' : 'Continue')}
        </OnboardingButton>
      </OnboardingButtonContainer>
    </>
  );
};

export type OnboardingLoginChallengeFormProps = OnboardingLoginFormProps &
  OnboardingLoginChallengeProps;

/**
 * Onboarding login challenge form. This form is used to submit the challenge response
 * when going through the custom login flow and the user is required to enter a code
 */
export const OnboardingLoginChallengeForm = React.forwardRef<
  FormikProps<OnboardingLoginChallengeFormValues>,
  OnboardingLoginChallengeFormProps
>(({ initialValues, handleSubmitDone, challengeName }, ref) => {
  const handleFormikSubmit = async (
    values: OnboardingLoginChallengeFormValues,
    _formikHelpers: FormikHelpers<OnboardingLoginChallengeFormValues>,
  ) => {
    await handleSubmitDone(values);
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={onboardingLoginChallengeFormSchema}
      onSubmit={handleFormikSubmit}
      innerRef={ref}
    >
      {({ handleSubmit }) => (
        <form noValidate onSubmit={handleSubmit} data-testid="testingform">
          <OnboardingLoginChallenge challengeName={challengeName} />
        </form>
      )}
    </Formik>
  );
});
